import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { changeNestedQuantity } from "@theme/cs-cart-decorations";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    this.addEventListener("cart:quantity-limit", this.#handleQuantityLimit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    this.removeEventListener("cart:quantity-limit", this.#handleQuantityLimit);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const row = this.refs.cartItemRows[line - 1];
    if (!row) return;

    const role = row.dataset.role;
    const bundleId = row.dataset.bundleId;

    // Only legacy standalone add-ons (old messages / gift wrapping) need
    // bundle cleanup. Shopify removes native children with their parent.
    const hasLegacyAddons = bundleId && this.refs.cartItemRows.some((item) =>
      item !== row && item.dataset.bundleId === bundleId && !item.dataset.parentKey
    );
    if (role === "main" && hasLegacyAddons) {
      return this.removeBundle(bundleId);
    }

    // Jinak standardní odstranění jen 1 řádku
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    // volitelně animace pro 1 řádek (jestli ji theme stejně dělá, vynech)
    // this.animateRowRemoval(line);
  }

  removeBundle(bundleId) {
    const cartPerformaceUpdateMarker =
      cartPerformance.createStartingMarker(`clear:user-action`);

    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // vyrob updates podle line-item keys
    const updates = {};
    this.refs.cartItemRows.forEach((r) => {
      if (r.dataset.bundleId === bundleId && r.dataset.key) {
        updates[r.dataset.key] = 0;
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    fetch("/cart/update.js", fetchConfig("json", { body }))
      .then((res) => res.text())
      .then((txt) => {
        const parsed = JSON.parse(txt);

        resetShimmer(this);

        if (parsed.errors) {
          // fallback: neřeším tu detailně, ale můžeš použít #handleCartError
          console.error(parsed.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsed.sections[this.sectionId],
          "text/html",
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.#updateQuantitySelectors(parsed);

        this.dispatchEvent(
          new CartUpdateEvent(parsed, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsed.sections,
          }),
        );

        morphSection(this.sectionId, parsed.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch(console.error)
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  animateRowRemoval(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key,
      ),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const row = this.refs.cartItemRows[line - 1];
    const request = quantity > 0 && row?.dataset.hasNestedAddons === 'true'
      ? changeNestedQuantity({
          parentKey: row.dataset.key,
          quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sectionsUrl: window.location.pathname,
          changeUrl: Theme.routes.cart_change_url,
          cartUrl: `${window.Shopify?.routes?.root || '/'}cart.js`,
        })
      : fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body })).then((response) => response.json());

    request.then((parsedResponseText) => {

        resetShimmer(this);

        const cartError = this.#getCartErrorMessage(parsedResponseText);

        if (cartError) {
          this.#handleCartError(line, { errors: cartError });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
        );

        this.#updateCartQuantitySelectorButtonStates();
        if (parsedResponseText.addonError) {
          this.#handleCartError(line, { errors: parsedResponseText.addonError });
        }
      })
      .catch((error) => {
        resetShimmer(this);
        this.#handleCartError(line, { errors: error.message });
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Nested decorations have no selector, so selector indices are not line indices.
    const quantityInput = this.refs.cartItemRows[line - 1]?.querySelector('input[data-cart-line]');
    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Displays a client-known inventory limit through the existing cart error UI.
   * @param {CustomEvent<{line: number, message: string}>} event
   */
  #handleQuantityLimit = (event) => {
    const { line, message } = event.detail || {};
    if (!line || !message) return;

    this.#handleCartError(line, { errors: message });
  };

  /**
   * Normalizes Shopify cart errors. Inventory errors from /cart/change.js are
   * returned in `description`, while validation errors can use `errors`.
   * @param {Record<string, any>} response - Shopify cart response.
   * @returns {string | null}
   */
  #getCartErrorMessage(response) {
    if (typeof response.errors === "string" && response.errors.trim()) {
      return response.errors;
    }

    if (response.errors && typeof response.errors === "object") {
      const messages = Object.values(response.errors)
        .flat()
        .filter((message) => typeof message === "string" && message.trim());

      if (messages.length > 0) return messages.join(" ");
    }

    if (typeof response.description === "string" && response.description.trim()) {
      return response.description;
    }

    if (response.status && typeof response.message === "string" && response.message.trim()) {
      return response.message;
    }

    return null;
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    const quantities = new Map();
    for (const item of updatedCart.items) {
      const id = item.variant_id.toString();
      quantities.set(id, (quantities.get(id) || 0) + item.quantity);
    }
    for (const [variantId, quantity] of quantities) {
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", quantity.toString());

        // Update the quantity selector's internal state
        if (
          "updateCartQuantity" in selector &&
          typeof selector.updateCartQuantity === "function"
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      "cart-quantity-selector-component",
    )) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
